import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Link, Box, IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, Divider, Button } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import logo_black from '../assets/logo_black.png';
import logo_white from '../assets/logo_white.png';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import useLanguage from '../useLanguage';
import { YouTube, X as XIcon } from '@mui/icons-material';
import { brand } from '../thema';

// PC 用メニューを表示する最小幅（プリレンダーした静的 HTML でも同じ幅で切り替える）
const DESKTOP_NAV_MIN_WIDTH = 1300;
const desktopNavQuery = `@media (min-width:${DESKTOP_NAV_MIN_WIDTH}px)`;

interface HeaderProps {
    textColor?: string;
    backgroundColor?: string;
    // true の場合、黒背景・白文字で固定する（トップ・お問い合わせ用）
    dark?: boolean;
}

const Header: React.FC<HeaderProps> = ({ textColor: textColorProp = 'inherit', backgroundColor: backgroundColorProp = 'transparent', dark = false }) => {
    const { t, i18n } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const theme = useTheme();
    // メニュー項目が多いため、1300px 未満ではドロワーメニューを使う
    const isMobile = useMediaQuery(theme.breakpoints.down(DESKTOP_NAV_MIN_WIDTH));
    const { language, changeLanguage } = useLanguage();

    // ロゴ画像の背景色（黒）に合わせて純黒にする
    const textColor = dark ? 'inherit' : textColorProp;
    const backgroundColor = dark ? '#000000' : backgroundColorProp;
    const borderBottom = dark ? `1px solid ${brand.lineDark}` : 'none';

    const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    const menuItems: { text: string; href: string; children?: { text: string; href: string }[] }[] = [
        { text: t('nav_services'), href: '/#services' },
        { text: t('nav_pricing'), href: '/#pricing' },
        { text: t('nav_works'), href: '/#works' },
        { text: t('nav_flow'), href: '/#flow' },
        { text: t('nav_faq'), href: '/#faq' },
        { text: t('company_info'), href: '/company' },
        { text: t('nav_message'), href: '/message' },
        //{ text: t('careers'), href: '/careers' },
        //{ text: t('news'), href: '/news' },
    ];

    const handleLocaleChange = (locale: string) => {
        changeLanguage(locale);
    };

    useEffect(() => {
        i18n.changeLanguage(language);
    }, [language, i18n]);
    
    return (
        <AppBar position="fixed" sx={{ backgroundColor: backgroundColor, boxShadow: 'none', zIndex: 10, borderBottom }} enableColorOnDark>
            <Toolbar sx={{ backgroundColor: 'transparent', display: 'flex', justifyContent: 'space-between', mt: dark ? 0 : 3 }}>
                <Link href="/" sx={{ display: 'flex', alignItems: 'center' }}>
                    {textColor === 'inherit' ?
                        (<img src={logo_white} alt="Reband Logo" width={100} height={30} />)
                        :
                        (<img src={logo_black} alt="Reband Logo" width={100} height={30} />)
                    }
                </Link>
                {isMobile ? (
                    <>
                        <IconButton edge="end" sx={{ color : textColor}} aria-label="menu" onClick={toggleDrawer(true)}>
                            <MenuIcon/>
                        </IconButton>
                        <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
                            <Box
                                sx={{ width: 250 }}
                                role="presentation"
                                onClick={toggleDrawer(false)}
                                onKeyDown={toggleDrawer(false)}
                            >
                                <List component="nav" aria-label="Global">
                                    {menuItems.map((item, index) => (
                                        <React.Fragment key={index}>
                                            <ListItem key={index} component="a" href={item.href}>
                                                <ListItemText primary={item.text} />
                                            </ListItem>
                                            {item.children && item.children.map((child, childIndex) => (
                                                <ListItem key={childIndex} component="a" href={child.href} sx={{ pl: 4 }}>
                                                    <ListItemText primary={child.text} />
                                                </ListItem>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                    <ListItem component="a" href="/contact">
                                        <ListItemText primary={t('contact')} />
                                    </ListItem>
                                </List>
                            </Box>
                            <Divider sx={{ mt: 1, mb: 3, mr: 2 }} />
                            <Box sx={{ display: 'flex', ml: 1 }}>
                                <IconButton href="https://www.youtube.com/@rebandinc" target="_blank" rel="noopener noreferrer" sx={{ color: '#111418', paddingRight: 0 }}>
                                    <YouTube fontSize="small"/>
                                </IconButton>
                                <IconButton href="https://x.com/rebandinc" target="_blank" rel="noopener noreferrer" sx={{ color: '#111418', paddingLeft: 2 }}>
                                    <XIcon fontSize="small"/>
                                </IconButton>
                            </Box>
                            <List sx={{ display: 'flex' }}>
                                <ListItem onClick={() => handleLocaleChange('ja')} sx={{ width: 'auto', paddingRight: 0 }}>
                                    <ListItemText primary={t('jp')} />
                                </ListItem>
                                <ListItem onClick={() => handleLocaleChange('en')} sx={{ width: 'auto', paddingLeft: 2 }}>
                                    <ListItemText primary={t('en')} />
                                </ListItem>
                            </List>
                        </Drawer>
                    </>
                ) : (
                    <>
                        <Box component="nav" aria-label="Global" sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'none', [desktopNavQuery]: { display: 'flex' }, gap: 4 }}>
                            {menuItems.map((item, index) => (
                                <Link key={index} href={item.href} color={textColor} underline="none" sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap', '&:hover': { opacity: 0.7 } }}>
                                    {item.text}
                                </Link>
                            ))}
                        </Box>
                        <Box sx={{ display: 'none', [desktopNavQuery]: { display: 'flex' }, gap: 2, alignItems: 'center', paddingRight: 2 }}>
                             <IconButton href="https://www.youtube.com/@rebandinc" target="_blank" rel="noopener noreferrer" sx={{ color: textColor === 'inherit' ? '#ffffff' : '#111418' }}>
                                <YouTube fontSize="small"/>
                            </IconButton>
                            <IconButton href="https://x.com/rebandinc" target="_blank" rel="noopener noreferrer" sx={{ color: textColor === 'inherit' ? '#ffffff' : '#111418' }}>
                                <XIcon fontSize="small"/>
                            </IconButton>
                            <Link href="#" color={textColor} underline="none" onClick={() => handleLocaleChange('ja')}>
                                {t('jp')}
                            </Link>
                            <Link href="#" color={textColor} underline="none" onClick={() => handleLocaleChange('en')}>
                                {t('en')}
                            </Link>
                            <Button
                                href="/contact"
                                variant="outlined"
                                sx={{
                                    color: textColor === 'inherit' ? '#ffffff' : brand.ink,
                                    borderColor: textColor === 'inherit' ? 'rgba(255, 255, 255, 0.6)' : brand.ink,
                                    borderRadius: 0,
                                    px: 2.5,
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        borderColor: textColor === 'inherit' ? '#ffffff' : brand.ink,
                                        backgroundColor: textColor === 'inherit' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(17, 20, 24, 0.04)',
                                    },
                                }}
                            >
                                {t('contact')}
                            </Button>
                        </Box>
                    </>
                )}
            </Toolbar>
        </AppBar>
    );
};

export default Header;